import { FAIL_CLOSED_AI_POLICY_DEFAULTS_V1 } from "@adpilot/shared";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormatters } from "@/lib/format";

interface RiskLegendTipProps {
  /** 生效阈值(策略快照);列表场景拿不到统一快照时省略,按系统默认值展示。 */
  thresholds?: { medium: number; high: number; critical: number; weeklyCritical: number };
  /** 行代表整个提案/运行(多条变更取最高一档)时为 true,补充聚合说明。 */
  aggregate?: boolean;
}

/** 风险等级判定规则说明——决策详情、待审批列表、运行记录共用。 */
export function RiskLegendTip({
  thresholds,
  aggregate = false,
}: RiskLegendTipProps): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatPercent } = useFormatters();
  const effective = thresholds ?? FAIL_CLOSED_AI_POLICY_DEFAULTS_V1.bidChangeRiskThresholds;
  const vars = {
    medium: formatPercent(effective.medium, 0),
    high: formatPercent(effective.high, 0),
    critical: formatPercent(effective.critical, 0),
    weekly: formatPercent(effective.weeklyCritical, 0),
  };
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={t("runDetail.decisionRecord.riskLegend.aria")}
              className="inline-flex text-muted-foreground/70 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <IconInfoCircle className="size-3.5" />
            </button>
          }
        />
        <TooltipContent className="max-w-[290px] items-start py-2.5" side="bottom">
          <div className="flex flex-col gap-1 text-left normal-case tracking-normal">
            <p className="font-semibold">{t("runDetail.decisionRecord.riskLegend.title")}</p>
            <p>{t("runDetail.decisionRecord.riskLegend.low", vars)}</p>
            <p>{t("runDetail.decisionRecord.riskLegend.medium", vars)}</p>
            <p>{t("runDetail.decisionRecord.riskLegend.high", vars)}</p>
            <p>{t("runDetail.decisionRecord.riskLegend.critical", vars)}</p>
            {aggregate ? <p>{t("runDetail.decisionRecord.riskLegend.aggregateNote")}</p> : null}
            <p className="text-background/70">
              {thresholds
                ? t("runDetail.decisionRecord.riskLegend.footer")
                : t("runDetail.decisionRecord.riskLegend.footerDefaults")}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
